import { CustomLayer } from '@nivo/line';
import { CustomLayerProps } from '@nivo/line';
import { PointTooltipProps } from '@nivo/line';
import { ResponsiveLine } from '@nivo/line';
import { Serie } from '@nivo/line';
import { BinScatterChartCrossSelectProps } from 'libs/end-to-end/src/bin-scatter';
import { BinScatterChartMultiSelectProps } from 'libs/end-to-end/src/bin-scatter';
import { BinScatterChartProps } from 'libs/end-to-end/src/bin-scatter';
import { BinScatterChartSerializableProps } from 'libs/end-to-end/src/bin-scatter';
import { BinScatterControlledVsUncontrolledProps } from 'libs/end-to-end/src/bin-scatter';
import { calculateSlope } from 'libs/end-to-end/src/root-cause-analysis';
import { RootCauseOaxacaBetaGraphGroupPlotData } from 'libs/end-to-end/src/root-cause-analysis';
import { RootCauseOaxacaBetaLineChartData } from 'libs/end-to-end/src/root-cause-analysis';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Point } from 'apps/sigma2-ui/src/constants/data';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';
import { ConfigColors } from 'libs/end-to-end/src/types/branding/colors';
import { PurpleCircleGroup } from 'apps/sigma2-ui/src/components/svg/PurpleCircle';
import { BlueRoundedTriangleGroup } from 'apps/sigma2-ui/src/components/svg/BlueRoundedTriangle';
import { z } from 'zod';
import { capitalize } from 'lodash';
import { KARLA_FONT_FAMILY } from 'libs/end-to-end/src/types/branding/fonts';
import { SvgProps } from 'apps/sigma2-ui/src/components/svg/types';
import { useDimensionConfig } from 'apps/sigma2-ui/src/lib/hooks/useDimensionConfig';
import { LevelDefinitionIconGroup } from 'apps/sigma2-ui/src/components/LevelDefinitionIcon';
import { MyMetricDefinition } from 'libs/end-to-end/src/my-metric-definition';
import { MultiLineChartAxisLegend } from 'apps/sigma2-ui/src/components/MultilineChartAxisLegend';
import { determineUnitOfMetric } from 'libs/end-to-end/src/metrics';
import { formatNumberForDisplayInUnits } from 'libs/end-to-end/src/number-formatting';
import { RESPONSIVE_LINE_MARGIN } from 'apps/sigma2-ui/src/lib/charts/constants';
import { RESPONSIVE_LINE_Y_AXIS_LABEL_OFFSET } from 'apps/sigma2-ui/src/lib/charts/constants';

const GroupOneColor = '#933EE5';
const GroupTwoColor = '#1AAEE0';

const CrossSelectGroup = z.union([
  z.literal('firstGroup'),
  z.literal('secondGroup'),
]);

type CrossSelectGroup = z.infer<typeof CrossSelectGroup>;

export const isControlledVsUncontrolledVariant = (
  props: BinScatterChartProps,
): props is BinScatterControlledVsUncontrolledProps => {
  return 'groupResult' in props;
};

const isCrossSelectVariant = (
  props: BinScatterChartProps,
): props is BinScatterChartCrossSelectProps => {
  return 'binScatterCrossSelectOutput' in props;
};

const isMultiselectVariant = (
  props: BinScatterChartProps,
): props is BinScatterChartMultiSelectProps => {
  return 'binScatterMultiSelectOutput' in props;
};

const generateTrendlinePoints = (
  trendline: {
    Y0: number;
    Y1: number;
  },
  yMin: number,
  yMax: number,
) => {
  const { Y0, Y1 } = trendline;
  const trendYMin = Math.min(Y0, Y1);
  const trendYMax = Math.max(Y0, Y1);

  const outputData: Point[] = [];

  for (let x = 0; x <= 100; x++) {
    const y = trendYMin + (trendYMax - trendYMin) * (x / 100);

    if (y < yMin || y > yMax) {
      continue;
    }

    outputData.push({ x, y });
  }

  return outputData;
};

const generateTrendlinePointsMxb = (trendline: {
  Y0: number;
  Y1: number;
  X0: number;
  X1: number;
  domainMinX: number;
  domainMaxX: number;
  domainMinY: number;
  domainMaxY: number;
}) => {
  const { Y0, Y1, X0, X1, domainMinX, domainMaxX, domainMinY, domainMaxY } =
    trendline;

  const outputData: Point[] = [];

  // compute the slope
  const slopeCalculation = calculateSlope({
    x0: X0,
    y0: Y0,
    x1: X1,
    y1: Y1,
  });

  if (slopeCalculation === 'indeterminate') {
    return [];
  }

  const m =
    slopeCalculation === 'horizontal'
      ? 0
      : slopeCalculation === 'vertical'
        ? NaN
        : slopeCalculation;

  // create an increment from lower x to upper x
  const increment = (domainMaxX - domainMinX) / 100;

  if (increment === 0) {
    const x = domainMinX;
    const y = Y0 + m * (domainMinX - X0);

    for (let i = 0; i < 100; i++) {
      outputData.push({ x, y });
    }

    return outputData;
  }

  // create a point for each increment starting at lower x and ending at upper x
  for (let x = domainMinX; x <= domainMaxX; x += increment) {
    const y = Y0 + m * (x - X0);

    if (y < domainMinY || y > domainMaxY) {
      continue;
    }

    outputData.push({ x, y });
  }

  return outputData;
};

interface BinScatterChartContext {
  props: BinScatterChartProps | null;
  yMin: number;
  yMax: number;
  focusedLine: string | null;
  metricDefinition: MyMetricDefinition | null;
}

const BinScatterChartContext = createContext<BinScatterChartContext>({
  props: null,
  yMin: 0,
  yMax: 100,
  focusedLine: null,
  metricDefinition: null,
});

interface ScatterLayerSymbol {
  x: number;
  y: number;
  symbolGroupFn: React.FC<SvgProps>;
}

// Additional layer for Nivo's ResponsiveLine component that displays corresponding scatter dots for a line
const ScatterLayer: CustomLayer = (props: CustomLayerProps) => {
  const xScale = props.xScale as (x: number) => number;
  const yScale = props.yScale as (y: number) => number;

  const { props: binScatterProps, focusedLine } = useContext(
    BinScatterChartContext,
  );

  const { allPrivilegedLevels, getLevelByColumnValue } = useDimensionConfig();

  if (!binScatterProps) {
    return <></>;
  }

  let symbols: ScatterLayerSymbol[] = [];

  if (isMultiselectVariant(binScatterProps)) {
    const { binScatterMultiSelectOutput, activeLevelKeys, dimension } =
      binScatterProps;
    const { results } = binScatterMultiSelectOutput;
    const lines = Object.entries(results).map(([level, line]) => {
      return {
        level,
        ...line,
      };
    });
    symbols = lines
      .map((line) => {
        const { level, coordinates } = line;
        const isFocused =
          allPrivilegedLevels
            .map((level) => level.columnValue)
            .includes(level) || focusedLine === level;

        if (!activeLevelKeys.includes(level)) {
          return [];
        }

        const levelDefinition = getLevelByColumnValue(dimension, level);

        if (!levelDefinition) {
          return [];
        }

        return coordinates.map((coordinate) => {
          const { X, Y } = coordinate;

          const symbol: ScatterLayerSymbol = {
            x: X,
            y: Y,
            symbolGroupFn: (props) => (
              <LevelDefinitionIconGroup
                levelDefinition={levelDefinition}
                focused={false}
                center={true}
                dimmed={!isFocused}
                {...props}
                group={{ ...props.group, transform: 'scale(1.5)' }}
              />
            ),
          };

          return symbol;
        });
      })
      .flat();
  } else if (isCrossSelectVariant(binScatterProps)) {
    const { binScatterCrossSelectOutput } = binScatterProps;
    const groups = binScatterCrossSelectOutput;

    if (groups) {
      const { Group1: firstGroup, Group2: secondGroup } = groups;

      symbols = [
        ...firstGroup.coordinates.map((coordinate) => {
          const { X, Y } = coordinate;

          return {
            x: X,
            y: Y,
            symbolGroupFn: () => (
              <PurpleCircleGroup
                center={true}
                wantsColor={GroupOneColor}
                group={{ transform: `scale(1.5)` }}
              />
            ),
          };
        }),
        ...secondGroup.coordinates.map((coordinate) => {
          const { X, Y } = coordinate;

          return {
            x: X,
            y: Y,
            symbolGroupFn: () => (
              <BlueRoundedTriangleGroup
                center={true}
                wantsColor={GroupTwoColor}
                group={{ transform: `scale(1.5)` }}
              />
            ),
          };
        }),
      ];
    } else {
      symbols = [];
    }
  }
  const allSymbols: React.ReactNode[] = [];
  let symbolKey = 0;

  for (const singleSymbol of symbols) {
    symbolKey++;
    const { symbolGroupFn, x, y } = singleSymbol;
    const scaledX = xScale(x);
    const scaledY = yScale(y);

    allSymbols.push(
      <g
        key={`Symbol${symbolKey}`}
        transform={`translate(${scaledX},${scaledY})`}
      >
        {symbolGroupFn({})}
      </g>,
    );
  }

  return <>{allSymbols}</>;
};

// Override for Nivo's built in line chart "lines" layer
// Baselines appear solid while other lines are dashed
const DashedSolidLine: CustomLayer = (props: CustomLayerProps) => {
  const { series, lineGenerator } = props;
  const xScale = props.xScale as (x: number) => number;
  const yScale = props.yScale as (y: number) => number;

  return series.map((line) => {
    const { id, data, color, baseLine, forceSolidLine } = line;
    const shouldHaveSolidLine = baseLine || forceSolidLine;
    const dashedLineConfig = {
      strokeDasharray: '4, 6',
      strokeWidth: 4,
    };

    const solidLineConfig = {
      strokeWidth: 4,
    };

    return (
      <path
        key={id}
        d={
          lineGenerator(
            data.map((d) => {
              const scaledX = xScale(
                typeof d.data.x === 'number' ? d.data.x : 0,
              );
              const scaledY = yScale(
                typeof d.data.y === 'number' ? d.data.y : 0,
              );

              return {
                x: typeof scaledX === 'number' ? scaledX : 0,
                y: typeof scaledY === 'number' ? scaledY : 0,
              };
            }),
          ) ?? undefined
        }
        fill="none"
        stroke={color}
        strokeLinecap="round"
        style={shouldHaveSolidLine ? solidLineConfig : dashedLineConfig}
      />
    );
  });
};

// Component that displays onHover over a specific dimension line
const ChartTooltip = (props: PointTooltipProps) => {
  const { props: binScatterProps, metricDefinition } = useContext(
    BinScatterChartContext,
  );
  const language = useLanguage();

  const { getLevelByColumnValue } = useDimensionConfig();

  if (!binScatterProps) {
    return null;
  }

  if (
    !isMultiselectVariant(binScatterProps) &&
    !isCrossSelectVariant(binScatterProps)
  ) {
    return null;
  }

  const { focusArea, dimension } = binScatterProps;
  const { point } = props;
  const { serieId } = point;

  const xValue =
    typeof point.data.xFormatted === 'number'
      ? point.data.xFormatted
      : parseFloat(point.data.xFormatted);

  const yValue =
    typeof point.data.yFormatted === 'number'
      ? point.data.yFormatted
      : parseFloat(point.data.yFormatted);

  let tooltipTitle: string;

  if (isMultiselectVariant(binScatterProps)) {
    const levelDefinition = getLevelByColumnValue(
      dimension,
      z.string().parse(serieId),
    );
    tooltipTitle = levelDefinition?.displayName ?? '';
  } else {
    const group = CrossSelectGroup.parse(serieId);
    tooltipTitle = group === 'firstGroup' ? 'Group 1' : 'Group 2';
  }

  return (
    <div className="p-4 font-sans text-left bg-white rounded-md filter shadow-8px bg-opacity-70 backdrop-blur-sm">
      <p className="font-sans text-xl font-bold ">{tooltipTitle}</p>

      <p>
        {metricDefinition &&
          language.scatterChartXTooltip({
            metricDefinition,
            index: xValue,
          })}
      </p>
      <p>
        {metricDefinition &&
          language.scatterChartYTooltip({
            metricDefinition,
            focusArea,
            dependentValue: yValue,
          })}
      </p>
    </div>
  );
};

export const BinScatterChart = (props: BinScatterChartProps) => {
  const language = useLanguage();
  const [focusedLine, setFocusedLine] = useState<string | null>(null);

  const {
    allPrivilegedLevels,
    getAllLevelsForDimension,
    getLevelsByColumnValueForDimension,
  } = useDimensionConfig();

  const levelsForDimension = useMemo(() => {
    return getAllLevelsForDimension(props.dimension);
  }, [getAllLevelsForDimension, props.dimension]);

  const levelsByColumnValue = useMemo(() => {
    return getLevelsByColumnValueForDimension(props.dimension);
  }, [getLevelsByColumnValueForDimension, props.dimension]);

  return (
    <BinScatterChartPure
      {...props}
      allPrivilegedLevels={allPrivilegedLevels}
      levelsForDimension={levelsForDimension}
      levelsByColumnValue={levelsByColumnValue}
      focusedLine={focusedLine}
      language={language}
      setFocusedLine={setFocusedLine}
    />
  );
};

export const BinScatterChartPure = ({
  levelsByColumnValue,
  levelsForDimension,
  allPrivilegedLevels,
  focusedLine,
  language,
  setFocusedLine,
  ...props
}: BinScatterChartSerializableProps & {
  language: ReturnType<typeof useLanguage>;
  setFocusedLine: (newFocusedLine: string | null) => void;
}) => {
  const groupResultData = useMemo(() => {
    if (isControlledVsUncontrolledVariant(props)) {
      if ('plotData' in props.groupResult) {
        const plotData = RootCauseOaxacaBetaGraphGroupPlotData.parse(
          props.groupResult.plotData,
        );
        return RootCauseOaxacaBetaLineChartData.parse(plotData.data);
      }

      return props.groupResult;
    }
  }, [props]);

  // TODO: The complexity of this code is greater than 10. Refer to https://eslint.org/docs/latest/rules/complexity
  const { yMin, yMax, data, yMinDisplay, yMaxDisplay } = useMemo(() => {
    let yCoordinates: number[] = [];

    if (isMultiselectVariant(props)) {
      const { binScatterMultiSelectOutput } = props;
      const { results } = binScatterMultiSelectOutput;
      const lines = Object.entries(results).map(([levelKey, line]) => {
        return {
          levelKey,
          ...line,
        };
      });

      yCoordinates = lines
        .map((line) => {
          return line.coordinates.map((coordinate) => {
            return coordinate.Y;
          });
        })
        .flat();
    } else if (isCrossSelectVariant(props)) {
      const { binScatterCrossSelectOutput } = props;
      const groups = binScatterCrossSelectOutput;

      if (groups) {
        const { Group1: firstGroup, Group2: secondGroup } = groups;

        yCoordinates = [
          ...firstGroup.coordinates.map((coordinate) => {
            return coordinate.Y;
          }),
          ...secondGroup.coordinates.map((coordinate) => {
            return coordinate.Y;
          }),
        ];
      }
    } else if (groupResultData) {
      yCoordinates.push(
        groupResultData.treatment_level.trendline.Y0,
        groupResultData.treatment_level.trendline.Y1,
        groupResultData.reference_level.trendline.Y0,
        groupResultData.reference_level.trendline.Y1,
      );
    }

    const yMinValue = yCoordinates.length === 0 ? 0 : Math.min(...yCoordinates);
    let yMaxValue = yCoordinates.length === 0 ? 100 : Math.max(...yCoordinates);

    if (yMinValue === yMaxValue) {
      yMaxValue = 100;
    }

    const yDeltaValue = yMaxValue - yMinValue;

    const yBufferValue = yDeltaValue * 0.15;
    const yMinDisplay = yMinValue - yBufferValue;
    const yMaxDisplay = yMaxValue + yBufferValue;

    const data: Serie[] = [];

    if (isMultiselectVariant(props)) {
      const { binScatterMultiSelectOutput, activeLevelKeys } = props;
      const { results } = binScatterMultiSelectOutput;
      const lines = Object.entries(results).map(([levelKey, line]) => {
        return {
          levelKey,
          ...line,
        };
      });

      for (const line of lines) {
        const { levelKey, trendline } = line;

        if (!activeLevelKeys.includes(levelKey)) {
          continue;
        }

        const points = generateTrendlinePoints(
          trendline,
          yMinDisplay,
          yMaxDisplay,
        );

        data.push({
          id: levelKey,
          data: points,
        });
      }
    } else if (isCrossSelectVariant(props)) {
      const { binScatterCrossSelectOutput } = props;
      const groups = binScatterCrossSelectOutput;

      if (groups) {
        data.push({
          id: 'firstGroup',
          data: generateTrendlinePoints(
            groups['Group1'].trendline,
            yMinDisplay,
            yMaxDisplay,
          ),
        });

        data.push({
          id: 'secondGroup',
          data: generateTrendlinePoints(
            groups['Group2'].trendline,
            yMinDisplay,
            yMaxDisplay,
          ),
        });
      }
    } else if (isControlledVsUncontrolledVariant(props)) {
      const xMin = Math.min(
        groupResultData!.reference_level.trendline.X0,
        groupResultData!.treatment_level.trendline.X0,
        groupResultData!.reference_level.trendline.X1,
        groupResultData!.treatment_level.trendline.X1,
      );

      const xMax = Math.max(
        groupResultData!.reference_level.trendline.X0,
        groupResultData!.treatment_level.trendline.X0,
        groupResultData!.reference_level.trendline.X1,
        groupResultData!.treatment_level.trendline.X1,
      );

      const firstGroupTrendlinePoints = generateTrendlinePointsMxb({
        ...groupResultData!.reference_level.trendline,
        domainMinX: xMin,
        domainMaxX: xMax,
        domainMinY: yMinDisplay,
        domainMaxY: yMaxDisplay,
      });

      if (firstGroupTrendlinePoints.length > 0) {
        data.push({
          id: 'firstGroup',
          data: firstGroupTrendlinePoints,
        });
      }

      const secondGroupTrendlinePoints = generateTrendlinePointsMxb({
        ...groupResultData!.treatment_level.trendline,
        domainMinX: xMin,
        domainMaxX: xMax,
        domainMinY: yMinDisplay,
        domainMaxY: yMaxDisplay,
      });

      if (secondGroupTrendlinePoints.length > 0) {
        data.push({
          id: 'secondGroup',
          data: secondGroupTrendlinePoints,
        });
      }
    }

    return {
      yMin: yMinValue,
      yMax: yMaxValue,
      yMinDisplay,
      yMaxDisplay,
      data,
    };
  }, [groupResultData, props]);

  const lineColorSetter = useCallback(
    (serie: Serie): string => {
      const { id: serieId } = serie;

      if (isMultiselectVariant(props)) {
        const levelDefinition = levelsByColumnValue[z.string().parse(serieId)];

        if (!levelDefinition) {
          return '#000000';
        }

        const isFocused = focusedLine === serieId;

        if (
          allPrivilegedLevels
            .map((levelDefinition) => levelDefinition.columnValue)
            .includes(levelDefinition?.columnValue)
        ) {
          return ConfigColors[levelDefinition.color].Primary.raw;
        }

        if (!isFocused) {
          return `${ConfigColors[levelDefinition.color].Primary.raw}60`;
        }

        return ConfigColors[levelDefinition.color].Primary.raw;
      } else if (isCrossSelectVariant(props)) {
        const group = CrossSelectGroup.parse(serieId);

        return group === 'firstGroup' ? GroupOneColor : GroupTwoColor;
      } else {
        const group = CrossSelectGroup.parse(serieId);

        const levels = levelsForDimension;

        const referenceLevelDefinition = levels.find(
          (l) => l.displayName === groupResultData!.reference_level.name,
        );

        const treatmentLevelDefinition = levels.find(
          (l) => l.displayName === groupResultData!.treatment_level.name,
        );

        if (group === 'firstGroup') {
          return referenceLevelDefinition
            ? ConfigColors[referenceLevelDefinition.color].Primary.raw
            : '#000000';
        } else {
          return treatmentLevelDefinition
            ? ConfigColors[treatmentLevelDefinition.color].Primary.raw
            : '#000000';
        }
      }
    },
    [
      props,
      levelsByColumnValue,
      focusedLine,
      allPrivilegedLevels,
      levelsForDimension,
      groupResultData,
    ],
  );

  const xLabelPre =
    'groupResult' in props && props.overridingXAxisText !== null
      ? props.overridingXAxisText
      : language['scatterChartXLabel']({
          metricDefinition: props.metricDefinition,
        });

  // uppercase the first letter of the label
  const xLabel = capitalize(xLabelPre);

  const yLabelPre =
    'groupResult' in props && props.overridingYAxisText
      ? props.overridingYAxisText
      : language['scatterChartYLabel']({
          focusAreaDefinition: props.focusArea,
          metricDefinition: props.metricDefinition,
        });

  // uppercase the first letter of the label
  const yLabel = capitalize(yLabelPre);

  const xDomainMin = isControlledVsUncontrolledVariant(props)
    ? Math.min(
        groupResultData!.treatment_level.trendline.X0,
        groupResultData!.treatment_level.trendline.X1,
        groupResultData!.reference_level.trendline.X0,
        groupResultData!.reference_level.trendline.X1,
      )
    : 0;

  const xDomainMax = isControlledVsUncontrolledVariant(props)
    ? Math.max(
        groupResultData!.treatment_level.trendline.X0,
        groupResultData!.treatment_level.trendline.X1,
        groupResultData!.reference_level.trendline.X0,
        groupResultData!.reference_level.trendline.X1,
      )
    : 100;

  const chartParentRef = React.useRef<HTMLDivElement>(null);
  const [chartKey, setChartKey] = React.useState(0);
  const [chartHeight, setChartHeight] = React.useState(0);

  // Nivo's responsive line is a little finicky about resizing properly, so we force a re-render.
  useEffect(() => {
    setChartKey((prevKey) => prevKey + 1);
  }, [chartHeight]);

  const chartAspectRatio = 502 / 926;

  const handleResize = useCallback(() => {
    const boundingRect = chartParentRef.current?.getBoundingClientRect() ?? {
      width: 0,
    };
    const fullWidth = boundingRect.width;

    const chartWidth =
      fullWidth - RESPONSIVE_LINE_MARGIN.left - RESPONSIVE_LINE_MARGIN.right;
    const chartHeight = chartAspectRatio * chartWidth;

    const fullHeight =
      chartHeight + RESPONSIVE_LINE_MARGIN.top + RESPONSIVE_LINE_MARGIN.bottom;

    setChartHeight(fullHeight);
  }, [chartAspectRatio]);

  // Capture the new chart height on window resize.
  useEffect(() => {
    window.addEventListener('resize', handleResize);

    handleResize();

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [handleResize]);

  return (
    /** This extra wrapper allows us to reliably detect the parent's width without the chart itself impacting the layout. **/
    <div
      className="relative"
      style={{ height: `${chartHeight}px` }}
      ref={chartParentRef}
      // This is to force a re-render when the chart height changes. This fixes a bug on dashboards where
      // the card would't adapt to the new height.
      key={chartHeight}
    >
      <div
        className="absolute left-0 top-0 w-full"
        // When you use responsive nivo charts, the parent container must have a defined height.
        style={{ height: `${chartHeight}px` }}
      >
        <BinScatterChartContext.Provider
          value={{
            props,
            yMin,
            yMax,
            focusedLine,
            metricDefinition: props.metricDefinition,
          }}
        >
          <ResponsiveLine
            key={chartKey}
            curve={'basis'}
            motionConfig={'gentle'}
            data={data}
            colors={lineColorSetter}
            margin={RESPONSIVE_LINE_MARGIN}
            xScale={{ type: 'linear', min: xDomainMin, max: xDomainMax }}
            yScale={{
              type: 'linear',
              min: yMinDisplay,
              max: yMaxDisplay,
              stacked: false,
              reverse: false,
            }}
            onMouseLeave={() => {
              setFocusedLine(null);
            }}
            onMouseMove={(point) => {
              const { serieId } = point;
              setFocusedLine(typeof serieId === 'string' ? serieId : null);
            }}
            tooltip={ChartTooltip}
            yFormat=" >-.2f"
            axisTop={null}
            axisRight={null}
            axisBottom={{
              tickSize: 5,
              tickPadding: 5,
              tickRotation: -45,
              legend: xLabel,
              legendOffset: 70,
              legendPosition: 'middle',
              format: (v) => v.toFixed(1),
            }}
            axisLeft={{
              tickSize: 5,
              tickPadding: 5,
              tickRotation: 0,
              legend: (
                <MultiLineChartAxisLegend legendText={yLabel} lineLength={26} />
              ),
              legendPosition: 'middle',
              legendOffset: RESPONSIVE_LINE_Y_AXIS_LABEL_OFFSET,
              format: (v) => {
                const units = determineUnitOfMetric({
                  isMetricBoolean: props.metricDefinition.isMetricBoolean,
                  knownMaximum: yMax,
                  metricFocusArea: props.focusArea.focusArea,
                  definitionUnit: props.metricDefinition.unit,
                });

                // Check if this is a percentage metric with displayAsPercentage flag set
                const isPercentageAlreadyMultiplied =
                  units === 'percentage' &&
                  isControlledVsUncontrolledVariant(props) &&
                  props.groupResult.visualizationAttributes
                    ?.displayAsPercentage === true;

                return formatNumberForDisplayInUnits({
                  units,

                  isPercentageAlreadyMultipliedBy100:
                    isPercentageAlreadyMultiplied,
                  value: v,
                });
              },
            }}
            enablePoints={false}
            pointSize={20}
            pointColor={{ theme: 'background' }}
            pointBorderWidth={2}
            pointBorderColor={{ from: 'serieColor' }}
            pointLabelYOffset={-12}
            enableCrosshair={'focusArea' in props}
            theme={{
              text: {
                fontFamily: KARLA_FONT_FAMILY,
                fontSize: 16,
              },
              axis: {
                domain: {
                  line: {
                    stroke: '#B8BABC',
                    strokeWidth: 2,
                  },
                },
                ticks: {
                  line: {
                    strokeWidth: 0,
                  },
                },
                legend: {
                  text: {
                    stroke: '#3B4046',
                    fontSize: 16,
                    strokeWidth: 0.2,
                  },
                },
              },
            }}
            useMesh={true}
            layers={[
              'grid',
              'markers',
              'axes',
              ScatterLayer,
              'areas',
              'crosshair',
              DashedSolidLine,
              'slices',
              'points',
              'mesh',
              'legends',
            ]}
          />
        </BinScatterChartContext.Provider>
      </div>
    </div>
  );
};
